import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "landlord" ? "/dashboard" : "/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(res.role === "landlord" ? "/dashboard" : "/portal", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center shadow-sm">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="font-display font-extrabold text-xl tracking-tight text-slate-900">VuZuRent</div>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500 mt-2">Manage your rentals, Section 8 breakdowns, and tenants in one place.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" data-testid="login-email" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password" className="h-11" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 text-sm font-semibold gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-btn"
            className="w-full h-11 text-sm font-semibold gap-2.5 bg-white hover:bg-slate-50">
            <svg className="h-[18px] w-[18px]" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            Continue with Google
          </Button>
          <p className="text-[11px] text-slate-400 mt-2 text-center">Works for your landlord account and any tenant email you've added.</p>
          <p className="text-xs text-slate-500 mt-6 text-center">New tenant? Use the invitation link your landlord emailed you.</p>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1720065609938-ec0e33ffd9ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwcmVudGFsJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkxMzQ0NTE3fDA&ixlib=rb-4.1.0&q=85"
          alt="Rental home" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="relative z-10 max-w-md px-10 text-white">
          <div className="inline-flex items-center rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold mb-5">
            For independent landlords
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight leading-snug">Rent, Section 8 & maintenance — finally simple.</h2>
          <p className="text-white/80 mt-4 text-sm leading-relaxed">
            Track every voucher and tenant copay, collect rent by card, Zelle, CashApp or Chase, and send
            professional notices in a tap.
          </p>
        </div>
      </div>
    </div>
  );
}
